import { type FC, type ReactNode, useCallback, useMemo } from "react";

import type { Completion } from "@codemirror/autocomplete";
import {
  schemaCompletionSource,
  type SQLConfig,
  StandardSQL,
} from "@codemirror/lang-sql";
import { LanguageSupport } from "@codemirror/language";
import { Button, ButtonGroup, Row, SparkleIcon, Text } from "@hightouch/htui";
import { format } from "sql-formatter";

import { Editor, type Props as EditorProps } from "src/components/editor";
import { formatterLanguageBySourceType } from "./format-source-sql";
import { IntegrationIcon } from "src/components/integrations/integration-icon";
import { type Maybe, useSourceObjectDefinitionsQuery } from "src/graphql";
import type { UnsavedValue } from "src/hooks/use-unsaved-value";
import { getObjectDefinitionKey } from "src/utils/sources";
import {
  QueryContainer,
  QueryHeader,
  QueryHeaderTitle,
} from "src/components/query";

export interface SqlEditorProps extends Omit<EditorProps, "language"> {
  /**
   * Line number to highlight with a red background, in case there's an error
   */
  highlightErroredLine?: number;

  /**
   * Source to show database schema autocomplete for
   */
  source:
    | Maybe<{
        id: string;
        type: string;
        name: string;
        definition: { name: string; icon: string; isSampleDataSource: boolean };
        columns?: never;
        traits?: never;
        relationships?: never;
      }>
    | undefined;

  /**
   * Result of `useUnsavedValue` hook to support storing drafts in local storage
   */
  unsavedValue?: UnsavedValue<string>;

  /**
   * Additional actions to show in the editor header
   */
  actions?: ReactNode;

  /**
   * Whether the editor should show the beautify button
   * @default true
   */
  isBeautifyable?: boolean;

  sqlConfigOverride?: SQLConfig;

  /**
   * Width of the editor
   *  - Defaults to 100%
   */
  width?: string;
}

export const SqlEditor: FC<SqlEditorProps> = ({
  highlightErroredLine: highlightErroredLineNumber,
  source,
  value,
  unsavedValue,
  onChange,
  actions,
  isBeautifyable = true,
  width = "100%",
  minHeight = "150px",
  sqlConfigOverride,
  ...props
}) => {
  const { data } = useSourceObjectDefinitionsQuery(
    { sourceId: String(source?.id) },
    {
      enabled: Boolean(source?.id),
    },
  );

  const config = useMemo<SQLConfig | undefined>(() => {
    if (sqlConfigOverride) {
      return sqlConfigOverride;
    }

    if (!source && !data) {
      return undefined;
    }

    if (!data) {
      return undefined;
    }

    const schema: Record<string, Completion[]> = {};
    const tables: Completion[] = [];

    for (const objectDefinition of data.object_definitions) {
      const schemaName = objectDefinition.object_definition_group?.name;

      const tableName = schemaName
        ? getObjectDefinitionKey(objectDefinition, {
            definition: { type: source?.type },
          })
        : objectDefinition.name;

      tables.push({
        label: tableName,
        type: "table",
      });

      schema[tableName] = [];

      for (const objectSchema of objectDefinition.object_schemas) {
        schema[tableName]?.push({
          label: objectSchema.key,
          type: "column",
        });
      }
    }

    return {
      schema,
      tables,
    };
  }, [source, data, sqlConfigOverride]);

  const language = useMemo(() => {
    if (!config || config?.tables?.length === 1000) {
      return new LanguageSupport(StandardSQL.language);
    }

    return new LanguageSupport(StandardSQL.language, [
      StandardSQL.language.data.of({
        autocomplete: schemaCompletionSource({
          ...config,
          defaultSchema: source?.columns ? "" : "public",
        }),
      }),
    ]);
  }, [config, source?.columns]);

  const changeValue = useCallback(
    (value: string) => {
      if (unsavedValue) {
        unsavedValue.set(value);
      }

      if (typeof onChange === "function") {
        onChange(value);
      }
    },
    [unsavedValue, onChange],
  );

  const restoreValue = useCallback(() => {
    if (!unsavedValue) {
      return;
    }

    if (
      typeof onChange === "function" &&
      typeof unsavedValue.value === "string"
    ) {
      onChange(unsavedValue.value);
    }

    unsavedValue.restore();
  }, [unsavedValue, onChange]);

  const formatterLanguage =
    source && source.type
      ? formatterLanguageBySourceType[source.type]
      : undefined;

  const beautify = useCallback(() => {
    if (!value) return;

    const formattedSql = format(value, {
      keywordCase: "upper",
      language: formatterLanguage,
    })
      // The format function often capitalizes the Handlebars `column` expression.
      // We need it to be lowercase so that it can be parsed correctly.
      .replace(/{{\s*COLUMN/g, "{{column");

    changeValue(formattedSql);
  }, [value, formatterLanguage, changeValue]);

  return (
    <QueryContainer
      width={width}
      minHeight={props.readOnly ? "250px" : undefined}
    >
      {!source?.columns && (
        <QueryHeader>
          <QueryHeaderTitle
            title={source?.name ?? "Private source"}
            icon={
              source?.definition && (
                <IntegrationIcon
                  src={source.definition.icon}
                  name={source.definition.name}
                />
              )
            }
          />

          <ButtonGroup>
            {isBeautifyable && formatterLanguage && (
              <Button isDisabled={!value} onClick={beautify} icon={SparkleIcon}>
                Beautify
              </Button>
            )}
            {actions}
          </ButtonGroup>
        </QueryHeader>
      )}

      {unsavedValue?.value && (
        <Row
          borderWidth="hairline"
          borderStyle="solid"
          borderColor="base.divider"
          p={2}
          bg="warning.background"
          align="center"
          justify="space-between"
        >
          <Text>You have unsaved changes from last session.</Text>
          <ButtonGroup>
            <Button onClick={unsavedValue.clear}>Dismiss</Button>
            <Button onClick={restoreValue}>Restore</Button>
          </ButtonGroup>
        </Row>
      )}

      <Editor
        bg="white"
        minHeight={minHeight}
        highlightErroredLine={highlightErroredLineNumber}
        language={language}
        value={value}
        onChange={onChange}
        {...props}
      />
    </QueryContainer>
  );
};
